import React, { Suspense } from "react";
import { RouteErrorBoundary } from "@/components/ui/ErrorBoundary";
import {
  RouteLoading,
  HomeScreenLoading,
  ProfileScreenLoading,
  FormScreenLoading,
  ListScreenLoading,
} from "@/components/ui/PageLoading";

// Lazy load all page components
// Auth module screens (features/auth/pages)
const LoginScreen = React.lazy(
  () => import("@/features/auth/pages/LoginScreen"),
);
const RegisterPhoneScreen = React.lazy(
  () => import("@/features/auth/pages/RegisterPhoneScreen"),
);
const VerifyOTPScreen = React.lazy(
  () => import("@/features/auth/pages/VerifyOTPScreen"),
);
const VerifyOTPMethodScreen = React.lazy(
  () => import("@/features/auth/pages/VerifyOTPMethodScreen"),
);
const ForgotPasswordScreen = React.lazy(
  () => import("@/features/auth/pages/ForgotPasswordScreen"),
);
const ResetPasswordScreen = React.lazy(
  () => import("@/features/auth/pages/ResetPasswordScreen"),
);
const RedirectRoleScreen = React.lazy(
  () => import("@/features/auth/pages/RedirectRoleScreen"),
);
const ChangePasswordScreen = React.lazy(
  () => import("@/features/auth/pages/ChangePasswordScreen"),
);

// Tenants module screens (features/players & organizers/pages) - RBAC, player/organizer profiles
const ProfileSetupScreen = React.lazy(
  () => import("@/features/players/pages/ProfileSetupScreen"),
);
const PlayerHomeScreen = React.lazy(
  () => import("@/features/players/pages/PlayerHomeScreen"),
);
const OrganizerHomeScreen = React.lazy(
  () => import("@/features/organizers/pages/OrganizerHomeScreen"),
);
const MyScheduleScreen = React.lazy(
  () => import("@/features/players/pages/MyScheduleScreen"),
);
const PlayerStatisticsScreen = React.lazy(
  () => import("@/features/players/pages/PlayerStatisticsScreen"),
);
const PlayerAchievementsScreen = React.lazy(
  () => import("@/features/players/pages/PlayerAchievementsScreen"),
);
const OrganizationStatisticsScreen = React.lazy(
  () => import("@/features/organizers/pages/OrganizationStatisticsScreen"),
);
const AccountScreen = React.lazy(
  () => import("@/features/common/pages/AccountScreen"),
);
const UpdateProfileScreen = React.lazy(
  () => import("@/features/players/pages/UpdateProfileScreen"),
);
const OrganizerRegistrationScreen = React.lazy(
  () => import("@/features/organizers/pages/OrganizerRegistrationScreen"),
);
const MatchHistoryScreen = React.lazy(
  () => import("@/features/players/pages/MatchHistoryScreen"),
);
const PlayerProfileScreen = React.lazy(
  () => import("@/features/players/pages/PlayerProfileScreen"),
);
const OrganizerProfileScreen = React.lazy(
  () => import("@/features/organizers/pages/OrganizerProfileScreen"),
);

// Common module screens (features/common/pages)
const AppHomeScreen = React.lazy(
  () => import("@/features/common/pages/AppHomeScreen"),
);
const SettingScreen = React.lazy(
  () => import("@/features/common/pages/SettingScreen"),
);
const SubscriptionScreen = React.lazy(
  () => import("@/features/common/pages/SubscriptionScreen"),
);
const ReportScreen = React.lazy(
  () => import("@/features/common/pages/ReportScreen"),
);

// Notifications module screens (features/notifications/pages)
const NotificationScreen = React.lazy(
  () => import("@/features/notifications/pages/NotificationScreen"),
);

// Tournaments module screens (features/tournaments/pages) - Tournament Core
const CreateTournamentScreen = React.lazy(
  () => import("@/features/tournaments/pages/CreateTournamentScreen"),
);
const TournamentDetailScreen = React.lazy(
  () => import("@/features/tournaments/pages/TournamentDetailScreen"),
);
const CompetitionDetailScreen = React.lazy(
  () => import("@/features/tournaments/pages/CompetitionDetailScreen"),
);
const CompetitionListScreen = React.lazy(
  () => import("@/features/tournaments/pages/CompetitionListScreen"),
);
const CompetitionScheduleScreen = React.lazy(
  () => import("@/features/tournaments/pages/CompetitionScheduleScreen"),
);
const CompetitionResultsScreen = React.lazy(
  () => import("@/features/results/pages/CompetitionResultsScreen"),
);

// Participants module screens (features/participants/pages) - Registration, approval
const RegistrationProgressScreen = React.lazy(
  () => import("@/features/participants/pages/RegistrationProgressScreen"),
);
const RegistrationListScreen = React.lazy(
  () => import("@/features/participants/pages/RegistrationListScreen"),
);

// Tournaments module screens (features/tournaments/pages) - Discovery and history
const RegisterTournamentScreen = React.lazy(
  () => import("@/features/tournaments/pages/RegisterTournamentScreen"),
);
const TournamentHistoryScreen = React.lazy(
  () => import("@/features/tournaments/pages/TournamentHistoryScreen"),
);
const MyTournamentScreen = React.lazy(
  () => import("@/features/tournaments/pages/MyTournamentScreen"),
);

// Matches module screens (features/matches/pages) - Scheduling, brackets
const EditBracketScreen = React.lazy(
  () => import("@/features/matches/pages/EditBracketScreen"),
);
const DrawPositionScreen = React.lazy(
  () => import("@/features/matches/pages/DrawPositionScreen"),
);

// Results module
const CategoryManagementScreen = React.lazy(
  () => import("@/features/results/pages/CategoryManagementScreen"),
);
const AddPrizeScreen = React.lazy(
  () => import("@/features/results/pages/AddPrizeScreen"),
);

// Ranking module
const LeaderBoardScreen = React.lazy(
  () => import("@/features/ranking/pages/LeaderBoardScreen"),
);
const RankingInformationScreen = React.lazy(
  () => import("@/features/ranking/pages/RankingInformationScreen"),
);
const PlayerRankingInformationScreen = React.lazy(
  () => import("@/features/ranking/pages/PlayerRankingInformationScreen"),
);
const RankingSettlementDetail = React.lazy(
  () => import("@/features/ranking/pages/RankingSettlementDetailScreen"),
);

// Lazy load layout components
const MainTabs = React.lazy(() => import("@/components/layout/MainTabs"));
const OrganizerTabs = React.lazy(
  () => import("@/components/layout/OrganizerTabs"),
);

// Higher-order component to wrap lazy components with appropriate loading states
const withLazyLoading = <P extends object>(
  Component: React.LazyExoticComponent<React.ComponentType<P>>,
  loadingType: "route" | "home" | "profile" | "form" | "list" = "route",
  fallbackMessage?: string,
) => {
  return React.memo((props: P) => {
    let LoadingComponent;
    switch (loadingType) {
      case "home":
        LoadingComponent = <HomeScreenLoading />;
        break;
      case "profile":
        LoadingComponent = <ProfileScreenLoading />;
        break;
      case "form":
        LoadingComponent = <FormScreenLoading />;
        break;
      case "list":
        LoadingComponent = <ListScreenLoading />;
        break;
      default:
        LoadingComponent = <RouteLoading message={fallbackMessage} />;
        break;
    }

    return (
      <RouteErrorBoundary>
        <Suspense fallback={LoadingComponent}>
          <Component {...props} />
        </Suspense>
      </RouteErrorBoundary>
    );
  });
};

// Export wrapped components with appropriate loading states
export const LazyLoginScreen = withLazyLoading(
  LoginScreen,
  "form",
  "Đang tải trang đăng nhập...",
);
export const LazyRegisterPhoneScreen = withLazyLoading(
  RegisterPhoneScreen,
  "form",
  "Đang tải trang đăng ký...",
);
export const LazyVerifyOTPScreen = withLazyLoading(
  VerifyOTPScreen,
  "form",
  "Đang tải trang xác thực...",
);
export const LazyVerifyOTPMethodScreen = withLazyLoading(
  VerifyOTPMethodScreen,
  "form",
  "Đang tải phương thức xác thực...",
);
export const LazyForgotPasswordScreen = withLazyLoading(
  ForgotPasswordScreen,
  "form",
  "Đang tải trang quên mật khẩu...",
);
export const LazyResetPasswordScreen = withLazyLoading(
  ResetPasswordScreen,
  "form",
  "Đang tải trang đặt lại mật khẩu...",
);
export const LazyProfileSetupScreen = withLazyLoading(
  ProfileSetupScreen,
  "form",
  "Đang tải thiết lập hồ sơ...",
);
export const LazyRedirectRoleScreen = withLazyLoading(
  RedirectRoleScreen,
  "route",
  "Đang chuyển hướng...",
);

// Home screens
export const LazyPlayerHomeScreen = withLazyLoading(
  PlayerHomeScreen,
  "home",
  "Đang tải trang chủ...",
);
export const LazyOrganizerHomeScreen = withLazyLoading(
  OrganizerHomeScreen,
  "home",
  "Đang tải trang chủ organizer...",
);
export const LazyAppHomeScreen = withLazyLoading(
  AppHomeScreen,
  "home",
  "Đang tải bảng tin...",
);

// List/Schedule screens
export const LazyMyScheduleScreen = withLazyLoading(
  MyScheduleScreen,
  "list",
  "Đang tải lịch thi đấu...",
);
export const LazyRegisterTournamentScreen = withLazyLoading(
  RegisterTournamentScreen,
  "list",
  "Đang tải danh sách giải đấu...",
);
export const LazyMatchHistoryScreen = withLazyLoading(
  MatchHistoryScreen,
  "list",
  "Đang tải lịch sử trận đấu...",
);
export const LazyRegistrationListScreen = withLazyLoading(
  RegistrationListScreen,
  "list",
  "Đang tải danh sách đăng ký...",
);
export const LazyTournamentHistoryScreen = withLazyLoading(
  TournamentHistoryScreen,
  "list",
  "Đang tải giải đấu đã tham gia...",
);
export const LazyMyTournamentScreen = withLazyLoading(
  MyTournamentScreen,
  "list",
  "Đang tải giải đấu đã tổ chức...",
);
export const LazyNotificationScreen = withLazyLoading(
  NotificationScreen,
  "list",
  "Đang tải thông báo...",
);

// Profile screens
export const LazyPlayerProfileScreen = withLazyLoading(
  PlayerProfileScreen,
  "profile",
  "Đang tải hồ sơ người chơi...",
);
export const LazyOrganizerProfileScreen = withLazyLoading(
  OrganizerProfileScreen,
  "profile",
  "Đang tải hồ sơ organizer...",
);
export const LazyUpdateProfileScreen = withLazyLoading(
  UpdateProfileScreen,
  "form",
  "Đang tải cập nhật hồ sơ...",
);

// Statistics screens
export const LazyPlayerStatisticsScreen = withLazyLoading(
  PlayerStatisticsScreen,
  "route",
  "Đang tải thống kê...",
);
export const LazyPlayerAchievementsScreen = withLazyLoading(
  PlayerAchievementsScreen,
  "list",
  "Đang tải thành tựu...",
);
export const LazyOrganizationStatisticsScreen = withLazyLoading(
  OrganizationStatisticsScreen,
  "route",
  "Đang tải thống kê tổ chức...",
);

// Settings screens
export const LazySettingScreen = withLazyLoading(
  SettingScreen,
  "list",
  "Đang tải cài đặt...",
);
export const LazyAccountScreen = withLazyLoading(
  AccountScreen,
  "profile",
  "Đang tải thông tin tài khoản...",
);
export const LazyChangePasswordScreen = withLazyLoading(
  ChangePasswordScreen,
  "form",
  "Đang tải đổi mật khẩu...",
);
export const LazySubscriptionScreen = withLazyLoading(
  SubscriptionScreen,
  "route",
  "Đang tải gói đăng ký...",
);
export const LazyReportScreen = withLazyLoading(
  ReportScreen,
  "form",
  "Đang tải trang phản hồi...",
);

// Tournament screens
export const LazyCreateTournamentScreen = withLazyLoading(
  CreateTournamentScreen,
  "form",
  "Đang tải tạo giải đấu...",
);
export const LazyTournamentDetailScreen = withLazyLoading(
  TournamentDetailScreen,
  "route",
  "Đang tải chi tiết giải đấu...",
);
export const LazyCompetitionDetailScreen = withLazyLoading(
  CompetitionDetailScreen,
  "route",
  "Đang tải chi tiết nội dung...",
);
export const LazyCompetitionListScreen = withLazyLoading(
  CompetitionListScreen,
  "list",
  "Đang tải danh sách nội dung...",
);
export const LazyCompetitionScheduleScreen = withLazyLoading(
  CompetitionScheduleScreen,
  "route",
  "Đang tải lịch đấu...",
);
export const LazyCompetitionResultsScreen = withLazyLoading(
  CompetitionResultsScreen,
  "route",
  "Đang tải kết quả...",
);
export const LazyEditBracketScreen = withLazyLoading(
  EditBracketScreen,
  "form",
  "Đang tải cấu hình sơ đồ...",
);

// Organizer screens
export const LazyOrganizerRegistrationScreen = withLazyLoading(
  OrganizerRegistrationScreen,
  "form",
  "Đang tải đăng ký organizer...",
);
export const LazyRegistrationProgressScreen = withLazyLoading(
  RegistrationProgressScreen,
  "route",
  "Đang tải tiến trình đăng ký...",
);
export const LazyDrawPositionScreen = withLazyLoading(
  DrawPositionScreen,
  "form",
  "Đang tải bốc thăm...",
);

// Results screens
export const LazyCategoryManagementScreen = withLazyLoading(
  CategoryManagementScreen,
  "route",
  "Đang tải sơ đồ thi đấu...",
);
export const LazyAddPrizeScreen = withLazyLoading(
  AddPrizeScreen,
  "route",
  "Đang tải gán giải thưởng...",
);

// Layout components
export const LazyMainTabs = withLazyLoading(
  MainTabs,
  "route",
  "Đang tải giao diện chính...",
);
export const LazyOrganizerTabs = withLazyLoading(
  OrganizerTabs,
  "route",
  "Đang tải giao diện organizer...",
);

export const LazyLeaderBoardScreen = withLazyLoading(
  LeaderBoardScreen,
  "route",
  "Đang tải giao diện leaderboard...",
);
export const LazyRankingInformationScreen = withLazyLoading(
  RankingInformationScreen,
  "route",
  "Đang tải thông tin xếp hạng...",
);
export const LazyPlayerRankingInformationScreen = withLazyLoading(
  PlayerRankingInformationScreen,
  "route",
  "Đang tải thông tin người chơi...",
);
export const LazyRankingSettlementDetail = withLazyLoading(
  RankingSettlementDetail,
  "route",
  "Đang tải chi tiết thay đổi ELO...",
);

// Export all lazy components for easy import
export {
  LoginScreen,
  RegisterPhoneScreen,
  VerifyOTPScreen,
  VerifyOTPMethodScreen,
  ForgotPasswordScreen,
  ResetPasswordScreen,
  ProfileSetupScreen,
  RedirectRoleScreen,
  PlayerHomeScreen,
  OrganizerHomeScreen,
  AppHomeScreen,
  MyScheduleScreen,
  PlayerStatisticsScreen,
  PlayerAchievementsScreen,
  OrganizationStatisticsScreen,
  NotificationScreen,
  SettingScreen,
  AccountScreen,
  UpdateProfileScreen,
  ChangePasswordScreen,
  SubscriptionScreen,
  ReportScreen,
  OrganizerRegistrationScreen,
  MatchHistoryScreen,
  RegisterTournamentScreen,
  CreateTournamentScreen,
  TournamentDetailScreen,
  CompetitionDetailScreen,
  RegistrationProgressScreen,
  RegistrationListScreen,
  CompetitionScheduleScreen,
  CompetitionResultsScreen,
  TournamentHistoryScreen,
  MyTournamentScreen,
  PlayerProfileScreen,
  OrganizerProfileScreen,
  EditBracketScreen,
  DrawPositionScreen,
  MainTabs,
  OrganizerTabs,
  CategoryManagementScreen,
  AddPrizeScreen,
  LeaderBoardScreen,
  RankingInformationScreen,
  PlayerRankingInformationScreen,
  RankingSettlementDetail,
};
